import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaMoneyBillWave, FaCalculator, FaInfoCircle, FaCheckCircle, FaQuestionCircle, FaChartBar, FaLightbulb, FaQuestion } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const IncomeTaxCalculator = () => {
  const [formData, setFormData] = useState({
    annualIncome: '',
    deductions: '',
    taxExemptions: '',
    taxRate: '20'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculateTax = (e) => {
    e.preventDefault();
    
    const { annualIncome, deductions, taxExemptions, taxRate } = formData;
    
    if (!annualIncome || !deductions || !taxExemptions) {
      setError('Please fill in all required fields');
      return;
    }

    const taxableIncome = parseFloat(annualIncome) - parseFloat(deductions) - parseFloat(taxExemptions);
    const taxAmount = (taxableIncome * parseFloat(taxRate)) / 100;
    const netIncome = taxableIncome - taxAmount;

    setResults({
      taxableIncome: taxableIncome.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      netIncome: netIncome.toFixed(2)
    });

    // Update chart data
    setChartData({
      options: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            colors: ['#333']
          }
        },
        xaxis: {
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        colors: ['#4361ee', '#8338ec', '#38b000']
      },
      series: [{
        name: 'Amount ($)',
        data: [
          { x: 'Taxable Income', y: parseFloat(taxableIncome) },
          { x: 'Tax Amount', y: parseFloat(taxAmount) },
          { x: 'Net Income', y: parseFloat(netIncome) }
        ]
      }]
    });

    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="bg-gray-100 p-4">
      <Helmet>
        <title>Income Tax Calculator</title>
        <meta name="description" content="Calculate your income tax with our easy-to-use calculator." />
        <meta property="og:title" content="Income Tax Calculator" />
        <meta property="og:description" content="Calculate your income tax with our easy-to-use calculator." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/path-to-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Income Tax Calculator" />
        <meta name="twitter:description" content="Calculate your income tax with our easy-to-use calculator." />
        <meta name="twitter:image" content="https://example.com/path-to-image.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-center">
            <FaMoneyBillWave className="text-4xl text-indigo-600 mr-4" />
            <h1 className="text-3xl font-bold">Income Tax Calculator</h1>
          </div>

          <form onSubmit={calculateTax} className="mt-8">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                <FaMoneyBillWave className="inline-block mr-2" /> Annual Income
              </label>
              <input
                type="number"
                id="annualIncome"
                name="annualIncome"
                value={formData.annualIncome}
                onChange={handleChange}
                placeholder="Enter your annual income"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600"
                required
              />
              <span className="text-xs text-gray-500">Your total annual income before taxes</span>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                <FaCalculator className="inline-block mr-2" /> Deductions
              </label>
              <input
                type="number"
                id="deductions"
                name="deductions"
                value={formData.deductions}
                onChange={handleChange}
                placeholder="Enter your deductions"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600"
                required
              />
              <span className="text-xs text-gray-500">Eligible deductions that reduce taxable income</span>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                <FaCheckCircle className="inline-block mr-2" /> Tax Exemptions
              </label>
              <input
                type="number"
                id="taxExemptions"
                name="taxExemptions"
                value={formData.taxExemptions}
                onChange={handleChange}
                placeholder="Enter tax exemptions"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600"
                required
              />
              <span className="text-xs text-gray-500">Tax exemptions you qualify for</span>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                <FaQuestionCircle className="inline-block mr-2" /> Tax Rate (%)
              </label>
              <input
                type="number"
                id="taxRate"
                name="taxRate"
                value={formData.taxRate}
                onChange={handleChange}
                placeholder="Enter tax rate"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600"
              />
              <span className="text-xs text-gray-500">Applicable tax rate as a percentage</span>
            </div>

            <button
              type="submit"
              className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-600"
            >
              Calculate Tax
            </button>

            {error && (
              <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}
          </form>
        </div>

        {results && (
          <div className="p-6">
            <div className="flex items-center mb-4">
              <h2 className="text-2xl font-bold">Results</h2>
              <FaChartBar className="text-4xl text-indigo-600 ml-4" />
            </div>
            
            <div className="mb-8">
              <Chart options={chartData?.options} series={chartData?.series} type="bar" height={350} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-indigo-50 p-4 rounded-md shadow-md">
                <div className="flex items-center">
                  <FaMoneyBillWave className="text-2xl text-indigo-600 mr-4" />
                  <div>
                    <h3 className="text-lg font-bold">Taxable Income</h3>
                    <p className="text-gray-700">${results.taxableIncome}</p>
                  </div>
                </div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-md shadow-md">
                <div className="flex items-center">
                  <FaCalculator className="text-2xl text-indigo-600 mr-4" />
                  <div>
                    <h3 className="text-lg font-bold">Tax Amount</h3>
                    <p className="text-gray-700">${results.taxAmount}</p>
                  </div>
                </div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-md shadow-md">
                <div className="flex items-center">
                  <FaCheckCircle className="text-2xl text-indigo-600 mr-4" />
                  <div>
                    <h3 className="text-lg font-bold">Net Income</h3>
                    <p className="text-gray-700">${results.netIncome}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg overflow-hidden mt-8 p-6">
        <h2 className="text-2xl font-bold mb-4">Key Features & Benefits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-indigo-50 p-4 rounded-md shadow-md">
            <div className="flex items-center">
              <FaLightbulb className="text-2xl text-indigo-600 mr-4" />
              <div>
                <h3 className="text-lg font-bold">Instant Calculations</h3>
                <p className="text-gray-700">Get your tax calculations instantly with our fast and accurate calculator.</p>
              </div>
            </div>
          </div>
          <div className="bg-indigo-50 p-4 rounded-md shadow-md">
            <div className="flex items-center">
              <FaChartBar className="text-2xl text-indigo-600 mr-4" />
              <div>
                <h3 className="text-lg font-bold">Visual Insights</h3>
                <p className="text-gray-700">Understand your tax breakdown better with our interactive bar graph.</p>
              </div>
            </div>
          </div>
          <div className="bg-indigo-50 p-4 rounded-md shadow-md">
            <div className="flex items-center">
              <FaCheckCircle className="text-2xl text-indigo-600 mr-4" />
              <div>
                <h3 className="text-lg font-bold">Comprehensive Results</h3>
                <p className="text-gray-700">See all your tax-related figures in one place, including taxable income and net income.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg overflow-hidden mt-8 p-6">
        <h2 className="text-2xl font-bold mb-4">How to Use</h2>
        <ol className="list-decimal list-inside">
          <li className="mb-2">Enter your annual income before taxes</li>
          <li className="mb-2">Input your eligible deductions</li>
          <li className="mb-2">Add any tax exemptions you qualify for</li>
          <li className="mb-2">Enter the applicable tax rate (default is 20%)</li>
          <li className="mb-2">Click "Calculate Tax" to see your results</li>
        </ol>
      </div>

      <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg overflow-hidden mt-8 p-6">
        <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-indigo-50 p-4 rounded-md shadow-md">
            <div className="flex items-center">
              <FaQuestion className="text-2xl text-indigo-600 mr-4" />
              <div>
                <h3 className="text-lg font-bold">What is taxable income?</h3>
                <p className="text-gray-700">Taxable income is your total income minus any eligible deductions and exemptions.</p>
              </div>
            </div>
          </div>
          <div className="bg-indigo-50 p-4 rounded-md shadow-md">
            <div className="flex items-center">
              <FaQuestion className="text-2xl text-indigo-600 mr-4" />
              <div>
                <h3 className="text-lg font-bold">What are deductions?</h3>
                <p className="text-gray-700">Deductions are expenses that can be subtracted from your income to reduce your tax liability.</p>
              </div>
            </div>
          </div>
          <div className="bg-indigo-50 p-4 rounded-md shadow-md">
            <div className="flex items-center">
              <FaQuestion className="text-2xl text-indigo-600 mr-4" />
              <div>
                <h3 className="text-lg font-bold">What are tax exemptions?</h3>
                <p className="text-gray-700">Tax exemptions are specific amounts that can be deducted from your taxable income.</p>
              </div>
            </div>
          </div>
          <div className="bg-indigo-50 p-4 rounded-md shadow-md">
            <div className="flex items-center">
              <FaQuestion className="text-2xl text-indigo-600 mr-4" />
              <div>
                <h3 className="text-lg font-bold">How is tax calculated?</h3>
                <p className="text-gray-700">Tax is calculated by applying the tax rate to your taxable income.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeTaxCalculator;